// httpPorts — the Workspace BC slice: the Library projection, the finder
// corpus, and the singleton workspace's Author name.

import { exhaustSwitchCase, Result } from "@bilda/utils";
import type { Claim } from "../../components/ClaimRow/ClaimRow.js";
import type {
    FinderDoc,
    FinderKind,
} from "../../components/Finder/finderIndex.js";
import type { PartOfRef } from "../../components/Navigation/types.js";
import { parseBibliography } from "../bibliography.js";
import { parseContainerFacts } from "../containerFacts.js";
import { parseDocumentFacts } from "../documentFacts.js";
import { mkMalformed } from "../externalError.js";
import {
    getClaims,
    getHead,
    getSearch,
    getState,
    openLogHeadStream,
} from "../httpClient.js";
import type { PortResult } from "../ports/result.js";
import type { ClaimQuery, WorkspacePorts } from "../ports/workspace.js";
import type {
    ContentHit,
    ContentSearchResult,
    ExportItem,
    FileItem,
    Library,
    LibraryActivityEntry,
    LibraryActivityKind,
    LibraryBootHealth,
    LibraryIssue,
    LibraryItem,
    LibrarySemanticSummary,
    MaterialItem,
    MaterialReadability,
    MaterialTemplate,
    ResourceFacts,
    SourceItem,
} from "../ports.js";
import { parseReadStatus } from "../sourceReadStatus.js";
import type { LogHead } from "../stateHeartbeat.js";
import { bool, fields, list, num, str } from "../wireShape.js";
import { parseClaim } from "./claims.js";
import { formatKind } from "./packaging.js";
import { parseProject } from "./project.js";
import { isBand } from "./quality.js";
import { command } from "./shared.js";

// `GET /state`, UNSCOPED (flattened-object-model-design.md): with no
// `x-project-id` header, the server already answers with the WHOLE
// WORKSPACE's resources (`routes/state.ts` — "Absent → the whole workspace"),
// so this needed no server change. Resources group by `type`: source→sources,
// material→materials, package→exports (a Export is the exportable artifact;
// there is no separate "export" resource kind), file→files. Deleted resources
// are not shown.

const aiAuthoredMark: { readonly aiAuthored: true } = { aiAuthored: true };

// A material's course (bd-lya); malformed or absent → no field.
const partOfOf = (raw: unknown): { readonly partOf?: PartOfRef } => {
    const f = fields(raw);
    const rootId = str(f?.get("rootId"));
    const title = str(f?.get("title"));
    return rootId === undefined || title === undefined
        ? {}
        : { partOf: { rootId, title } };
};

const plannedMark: { readonly planned: true } = { planned: true };
const suggestionMark: { readonly suggestion: true } = { suggestion: true };

// A material's parts in reading order; non-string entries drop out, and none
// at all → no field.
const partsOf = (raw: unknown): { readonly parts?: readonly string[] } => {
    const parts = list(raw).flatMap((p) => str(p) ?? []);
    return parts.length === 0 ? {} : { parts };
};

// A material row's readability read (C4): `{ lix, band }`, both required and
// the band one of the five known LIX bands. Anything else (absent, an older
// server, shape drift) parses to undefined, so the row shows no chip.
const parseReadability = (raw: unknown): MaterialReadability | undefined => {
    const f = fields(raw);
    const lix = num(f?.get("lix"));
    const band = str(f?.get("band"));
    return lix === undefined || !isBand(band) ? undefined : { lix, band };
};

// The template a library row names, from its id (a freeform or an unknown
// template names none).
const parseTemplate = (
    id: string | undefined,
): MaterialTemplate | undefined => {
    switch (id) {
        case "T_quiz":
        case "T_pqbl_torus":
            return "quiz";
        case "T_question":
            return "question";
        default:
            return undefined;
    }
};

// A `{ id, title }` lineage ref's title.
const titleOf = (raw: unknown): string | undefined =>
    str(fields(raw)?.get("title"));

// One `/state` resource row -> its library item, parsed at the boundary. A
// row without an id or of an unknown kind (a deleted tombstone, a future
// kind) is undefined. Raw files are library items too (the `type:file`
// filter and the Files tab list them; the item view offers "make it a
// source").
export const libraryItemOf = (raw: unknown): LibraryItem | undefined => {
    const r = fields(raw);
    const id = str(r?.get("id"));
    const title = str(r?.get("title"));
    if (id === undefined) return undefined;
    // The row facts every kind shares (recency, creation for `created:`,
    // and how many materials derive from it).
    const facts: ResourceFacts = {
        lastEditedAt: str(r?.get("lastEditedAt")),
        createdAt: str(r?.get("createdAt")),
        usedIn: num(r?.get("usedIn")),
    };
    switch (str(r?.get("type"))) {
        case "source":
            return {
                type: "source",
                id,
                title,
                ...facts,
                bibliography: parseBibliography(r?.get("bibliography")),
                displayTitle: str(r?.get("displayTitle")),
                readStatus: parseReadStatus(r?.get("readStatus")),
                facts: num(r?.get("facts")),
            };
        case "material":
            return {
                type: "material",
                id,
                title,
                ...facts,
                groundingRevisedAt: str(r?.get("groundingRevisedAt")),
                readability: parseReadability(r?.get("readability")),
                template: parseTemplate(str(r?.get("templateId"))),
                basedOn: list(r?.get("derivedFrom")).flatMap(
                    (ref) => titleOf(ref) ?? [],
                ),
                versionCount: num(r?.get("versionCount")),
                ...(bool(r?.get("aiAuthored")) ? aiAuthoredMark : {}),
                ...partOfOf(r?.get("partOf")),
                ...partsOf(r?.get("parts")),
                ...parseContainerFacts(r?.get("container")),
                ...(bool(r?.get("planned")) ? plannedMark : {}),
                ...(bool(r?.get("suggestion")) ? suggestionMark : {}),
                ...parseDocumentFacts(r?.get("document")),
            };
        case "export":
            return {
                type: "export",
                id,
                title,
                ...facts,
                format: formatKind(str(r?.get("packagingId"))),
                packagedFrom: titleOf(r?.get("packagedFrom")),
            };
        case "file":
            return { type: "file", id, title, ...facts };
        default:
            return undefined;
    }
};

const loadLibrary = async (): PortResult<Library> => {
    const rState = await getState();
    if (Result.isErr(rState)) return rState;

    const sources: SourceItem[] = [];
    const materials: MaterialItem[] = [];
    const exports: ExportItem[] = [];
    const files: FileItem[] = [];
    // Archived resources (F86) are held out of the three default buckets and
    // collected here so the shell can offer them under an "Archived" filter.
    const archived: LibraryItem[] = [];
    for (const raw of list(fields(rState.value)?.get("resources"))) {
        const item = libraryItemOf(raw);
        if (item === undefined) continue;
        if (bool(fields(raw)?.get("archived"))) {
            archived.push(item);
            continue;
        }
        if (item.type === "source") sources.push(item);
        else if (item.type === "material") materials.push(item);
        else if (item.type === "export") exports.push(item);
        else files.push(item);
    }
    // Landing-surface reads (P45a): the recent-activity feed and the
    // needs-attention pointers, folded server-side from the same scoped
    // projection. Rows whose discriminant does not parse to a known kind drop
    // out (forward-compat, the same posture as every other list read).
    const activity: LibraryActivityEntry[] = [];
    for (const raw of list(fields(rState.value)?.get("activity"))) {
        const f = fields(raw);
        const id = str(f?.get("id"));
        const kind = parseActivityKind(str(f?.get("kind")));
        if (id === undefined || kind === undefined) continue;
        activity.push({
            id,
            kind,
            title: str(f?.get("title")),
            at: str(f?.get("at")),
        });
    }
    const issues: LibraryIssue[] = [];
    for (const raw of list(fields(rState.value)?.get("issues"))) {
        const f = fields(raw);
        const resourceId = str(f?.get("resourceId"));
        const count = num(f?.get("count"));
        if (str(f?.get("type")) !== "proposals") continue;
        if (resourceId === undefined || count === undefined) continue;
        issues.push({
            type: "proposals",
            resourceId,
            title: str(f?.get("title")),
            count,
        });
    }
    // Boot-health summary (`value.replayDegradation`, M7 phase 2). Counts
    // only — the two array lengths. Absent block degrades to a clean boot
    // (zero + zero), the same forward-compat posture as the reads above.
    const rd = fields(fields(rState.value)?.get("replayDegradation"));
    const bootHealth: LibraryBootHealth = {
        frozenAggregates: list(rd?.get("degradedAggregates")).length,
        unroutableEvents: list(rd?.get("unroutableEvents")).length,
    };
    // The semantic-summary READ path (`value.summary`, IDD 94442b9d):
    // consent + the latest generated version, both server-projected. An
    // absent block (older server, or a fetch shape drift) degrades to the
    // honest consent-off/never-generated floor — never a faked "on".
    const sf = fields(fields(rState.value)?.get("summary"));
    const currentF = fields(sf?.get("current"));
    const currentText = str(currentF?.get("text"));
    const summary: LibrarySemanticSummary = {
        consentEnabled: bool(sf?.get("consentEnabled")),
        current:
            currentText === undefined
                ? null
                : {
                      text: currentText,
                      composedAt: str(currentF?.get("placedAt")) ?? "",
                  },
    };
    return Result.ok({
        // The RAW server value (F56): the source of truth is the core
        // Workspace aggregate's `name`, projected onto `/state`. Empty ("")
        // when the workspace is unnamed (born so) — the shell derives a
        // display default ("<author>s arbetsyta") from it, NOT here, so this
        // stays the honest raw value with no fabricated fallback.
        workspaceName: str(fields(rState.value)?.get("workspaceName")) ?? "",
        sources,
        materials,
        exports,
        files,
        archived,
        activity,
        issues,
        bootHealth,
        summary,
        projects: list(fields(rState.value)?.get("projects")).flatMap(
            parseProject,
        ),
    });
};

// The content-finder's corpus. HONEST DEGRADATION (flagged, not faked): no
// server route projects resource BODIES yet, so over HTTP the finder searches
// TITLES only — real data (from the library read), never a fabricated `ok`.
// Each library item becomes a title-only `FinderDoc`; wiring body search needs
// a server-side content projection (a fold over source content + material
// element trees), the app-layer `buildFinderIndex` the design doc anticipates.
const loadFinderIndex = async (): PortResult<readonly FinderDoc[]> => {
    const rLib = await loadLibrary();
    if (Result.isErr(rLib)) return rLib;
    const lib = rLib.value;
    const titleDoc = (
        item:
            | Library["sources"][number]
            | Library["materials"][number]
            | Library["exports"][number],
    ): FinderDoc => {
        const kind: FinderKind = item.type;
        return {
            resourceId: item.id,
            kind,
            title: item.title ?? "",
            lines: [],
        };
    };
    return Result.ok([
        ...lib.sources.map(titleDoc),
        ...lib.materials.map(titleDoc),
        ...lib.exports.map(titleDoc),
    ]);
};

// One wire `kind` string → the typed activity-kind sum, or undefined for
// anything outside the known milestones (an unknown/future kind drops).
const parseActivityKind = (
    kind: string | undefined,
): LibraryActivityKind | undefined => {
    switch (kind) {
        case "ingested":
        case "authored":
        case "packaged":
            return kind;
        default:
            return undefined;
    }
};

// Persist the Author's name to the domain (singleton Workspace) via the
// `setAuthorName` command. No project scope applies. Reconciles the
// device-local display name with the `author_name_set` event so the agent's
// prompt (buildAuthorPrompt) shares the same name.

const setAuthorName = async (name: string): PortResult<void> => {
    const r = await command("setAuthorName", { authorName: name });
    return Result.isErr(r) ? r : Result.ok(undefined);
};

// Persist the Author's semantic-summary consent (IDD 94442b9d) via the
// `setSummaryConsent` command. `GET /state` now reads the real value back
// (`loadLibrary`'s `summary.consentEnabled`) — the caller still mirrors the
// value device-locally alongside this dispatch as an instant-paint cache,
// same split as `setAuthorName`.
const setSummaryConsent = async (consented: boolean): PortResult<void> => {
    const r = await command("setSummaryConsent", { consented });
    return Result.isErr(r) ? r : Result.ok(undefined);
};

// Accept the current privacy + copyright terms (the consent gate) via the
// `acceptPrivacyTerms` command. Until then every data route answers 403
// `privacy_terms_not_accepted` (External.PrivacyTermsNotAccepted).
const acceptPrivacyTerms = async (): PortResult<void> => {
    const r = await command("acceptPrivacyTerms", {});
    return Result.isErr(r) ? r : Result.ok(undefined);
};

// Rename the singleton Workspace via the `renameWorkspace` command (F56).
// The name projects back onto `GET /state`.value.workspaceName (the source
// of truth) and syncs the denormalized auth row the switcher reads. `''` is
// the honest unnamed state.
const renameWorkspace = async (name: string): PortResult<void> => {
    const r = await command("renameWorkspace", { name });
    return Result.isErr(r) ? r : Result.ok(undefined);
};

// Content search (C3). Parse at the boundary: a hit missing its id, kind,
// title or snippet is dropped (never a crash); `page` only when a number.
const parseContentHit = (raw: unknown): ContentHit | undefined => {
    const f = fields(raw);
    const resourceId = str(f?.get("resourceId"));
    const kind = str(f?.get("kind"));
    const title = str(f?.get("title"));
    const s = fields(f?.get("snippet"));
    const before = str(s?.get("before"));
    const match = str(s?.get("match"));
    const after = str(s?.get("after"));
    if (
        resourceId === undefined ||
        (kind !== "source" && kind !== "material") ||
        title === undefined ||
        before === undefined ||
        match === undefined ||
        after === undefined
    ) {
        return undefined;
    }
    const page = num(f?.get("page"));
    return {
        resourceId,
        kind,
        title,
        snippet: { before, match, after },
        ...(page === undefined ? {} : { page }),
        matchCount: num(f?.get("matchCount")) ?? 1,
    };
};

const searchContent = async (
    query: string,
): PortResult<ContentSearchResult> => {
    const r = await getSearch(query);
    if (Result.isErr(r)) return r;
    const value = fields(r.value);
    return Result.ok({
        hits: list(value?.get("hits")).flatMap((h) => {
            const hit = parseContentHit(h);
            return hit === undefined ? [] : [hit];
        }),
        truncated: bool(value?.get("truncated")),
    });
};

const claimsRequest = (query: ClaimQuery) => {
    switch (query.type) {
        case "claims_of_source":
            return getClaims("source", query.sourceId);
        case "claims_for_goal":
            return getClaims("goal", query.goalId);
        case "claims_about_concept":
            return getClaims("concept", query.conceptId);
        default:
            return exhaustSwitchCase(query);
    }
};

const loadClaims = async (query: ClaimQuery): PortResult<readonly Claim[]> => {
    const r = await claimsRequest(query);
    return Result.isErr(r) ? r : Result.ok(list(r.value).flatMap(parseClaim));
};

const loadLogHead = async (): PortResult<LogHead> => {
    const r = await getHead();
    if (Result.isErr(r)) return r;
    const head = num(fields(r.value)?.get("head"));
    return head === undefined
        ? Result.err(mkMalformed("head: expected a number"))
        : Result.ok(head);
};

// The browser's EventSource reconnects by itself after a dropped connection,
// but gives up for good on an error answer (a deploy's 502, terms not yet
// accepted): then it is reopened here, waiting longer each time up to a
// minute. The server sends `moved` on every (re)connect, so a gap is caught
// up; meanwhile the heartbeat stands in.
const REOPEN_FIRST_MS = 2_000;
const REOPEN_MAX_MS = 60_000;

const watchLogHead = (onMoved: () => void): (() => void) => {
    let source: EventSource | undefined;
    let reopen: ReturnType<typeof setTimeout> | undefined;
    let wait = REOPEN_FIRST_MS;
    const open = () => {
        const s = openLogHeadStream();
        source = s;
        s.addEventListener("moved", () => onMoved());
        s.onopen = () => {
            wait = REOPEN_FIRST_MS;
        };
        s.onerror = () => {
            if (s.readyState !== EventSource.CLOSED) return;
            reopen = setTimeout(open, wait);
            wait = Math.min(wait * 2, REOPEN_MAX_MS);
        };
    };
    open();
    return () => {
        clearTimeout(reopen);
        source?.close();
    };
};

export const workspaceHttp: WorkspacePorts = {
    loadLibrary,
    loadLogHead,
    watchLogHead,
    loadFinderIndex,
    searchContent,
    loadClaims,
    setAuthorName,
    setSummaryConsent,
    acceptPrivacyTerms,
    renameWorkspace,
};
